import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { validateCPF, validatePhone } from "@/utils/functions";
import { setCustomer } from "@/redux/ducks/customer";
import { ReducersTypes } from "@/redux/reducers/Models";
import { NavigateFunction } from "react-router-dom";

const personalDataSchema = z.object({
  name: z.string().min(2, "Nome obrigatório"),
  document: z.string().regex(/^\d{3}\.\d{3}\.\d{3}-\d{2}$/, "CPF inválido").refine(val => validateCPF(val), "CPF inválido"),
  email: z.string().email("E-mail inválido"),
  phone: z.string().regex(/^\(\d{2}\) \d{5}-\d{4}$/, "Celular inválido").refine(val => validatePhone(val), "Celular inválido"),
  isAcceptedTerms: z.boolean().refine(val => val === true, "Você deve aceitar os Termos e Condições de Uso"),
  isAcceptedPrivacy: z.boolean().refine(val => val === true, "Você deve aceitar a Política de Privacidade"),
});

export default function usePersonalDataForm(navigate: NavigateFunction) {
  const dispatch = useDispatch();

  const customer = useSelector((state: ReducersTypes) => state.customerReducer);

  const unifiedSchema = personalDataSchema;
  type FormData = z.infer<typeof unifiedSchema>;

  const defaultValues: FormData = { 
    name: customer?.register?.name ?? "", 
    document: customer?.register?.document ?? "", 
    email: customer?.register?.email ?? "", 
    phone: customer?.register?.phone ?? "", 
    isAcceptedTerms: false,
    isAcceptedPrivacy: false,
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<FormData>({
    resolver: zodResolver(unifiedSchema),
    mode: "onChange",
    defaultValues,
  });

  const watchedValues = watch();
  const { name, document, email, phone, isAcceptedTerms, isAcceptedPrivacy } = watchedValues;

  const onSubmit = useCallback(
    (data: FormData) => {
      dispatch(
        setCustomer({
          ...customer,
          register: {
            ...customer?.register,
            ...data,
          }
        })
      );
      navigate("/register/choosepin");
      reset();
    },
    [dispatch, reset, customer, navigate]
  );

  
  const isNameValid = name && name.trim().length >= 2;
  const isDocumentValid = document && document.length === 14 && validateCPF(document);
  const isEmailValid = email && email.includes('@') && email.includes('.');
  const isPhoneValid = phone && phone.length === 15 && validatePhone(phone);
  
  const areCheckboxesChecked = isAcceptedTerms === true && isAcceptedPrivacy === true;

  const isFormValid = isNameValid && isDocumentValid && isEmailValid && isPhoneValid && areCheckboxesChecked;

  return {
    isDisabled: !isFormValid,
    register,
    handleSubmit: handleSubmit(onSubmit),
    errors,
    watch,
    setValue,
  };
} 